<template>
  <gp-select
    v-model="model"
    :items="wrappedItems"
    :get-key="categoryKey"
    :get-label="categoryLabel"
    :get-value="unwrapValue"
    :placeholder="placeholder"
    :name="name"
    :searchable="searchable"
    :search-placeholder="searchPlaceholder"
    test-id="support-category"
  >
    <template
      #panel="{
        visibleItems,
        highlighted,
        idFor,
        isSelected,
        select,
        highlight,
      }"
    >
      <!-- General -->
      <div
        v-if="
          visibleItems.some((item) => (item as Wrapped).group === 'general')
        "
        class="mb-3"
      >
        <h4 class="text-sm text-rhino mb-2">
          {{ $t('support/category/uncategorized') }}
        </h4>
        <div class="flex flex-wrap gap-2">
          <template
            v-for="(item, idx) in visibleItems"
            :key="(item as Wrapped).cat.id"
          >
            <button
              v-if="(item as Wrapped).group === 'general'"
              :id="idFor(idx)"
              type="button"
              role="option"
              data-testid="support-category-option"
              :aria-selected="isSelected(item)"
              class="rounded-md px-4 py-2 text-sm transition focus-visible:outline-brand cursor-pointer"
              :class="[
                isSelected(item)
                  ? 'bg-brand text-onyx'
                  : 'bg-depth-2/60 text-mouse ',
                (highlighted as number) === idx ? 'outline outline-brand' : '',
              ]"
              @click="select(item)"
              @mouseenter="highlight(idx)"
              @focusin="highlight(idx)"
            >
              {{ labelFor((item as Wrapped).cat) }}
            </button>
          </template>
        </div>
      </div>

      <!-- Games -->
      <div
        v-if="visibleItems.some((item) => (item as Wrapped).group === 'games')"
      >
        <h4 class="text-sm text-rhino mb-2">
          {{ $t('support/category/games') }}
        </h4>
        <div
          class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2"
        >
          <template
            v-for="(item, idx) in visibleItems"
            :key="(item as Wrapped).cat.id"
          >
            <button
              v-if="(item as Wrapped).group === 'games'"
              :id="idFor(idx)"
              type="button"
              role="option"
              data-testid="support-category-option"
              :aria-selected="isSelected(item)"
              class="w-full text-left rounded-md px-4 py-2 text-sm transition focus-visible:outline-brand cursor-pointer"
              :class="[
                isSelected(item)
                  ? 'bg-brand text-onyx'
                  : 'bg-depth-2/60 text-mouse ',
                (highlighted as number) === idx ? 'outline outline-brand' : '',
              ]"
              @click="select(item)"
              @mouseenter="highlight(idx)"
              @focusin="highlight(idx)"
            >
              {{ labelFor((item as Wrapped).cat) }}
            </button>
          </template>
        </div>
      </div>

      <!-- No results -->
      <div v-if="searchable && !visibleItems.length" class="text-rhino text-sm">
        {{ $t('general/no_results') || 'No results' }}
      </div>
    </template>
  </gp-select>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import GpSelect from '~/components/support/GpSelect.vue';
import type { TicketCategory } from '~/store/support';

const model = defineModel<TicketCategory | null>({ default: null });

const props = withDefaults(
  defineProps<{
    general?: TicketCategory[];
    games?: TicketCategory[];
    placeholder?: string;
    name?: string;
    searchable?: boolean;
    searchPlaceholder?: string;
  }>(),
  {
    general: () => [],
    games: () => [],
    placeholder: '',
    name: 'category',
    searchable: true,
    searchPlaceholder: '',
  },
);

const { t } = useI18n();

type Wrapped = { cat: TicketCategory; group: 'general' | 'games' };

const wrappedItems = computed<Wrapped[]>(() => [
  ...(props.general ?? []).map((c) => ({ cat: c, group: 'general' as const })),
  ...(props.games ?? []).map((c) => ({ cat: c, group: 'games' as const })),
]);

const labelFor = (opt: TicketCategory): string => {
  const key = opt.locoKey;
  return key ? t(`support/category/${key}`) : opt.name;
};

const isWrapped = (item: unknown): item is Wrapped =>
  !!item &&
  typeof item === 'object' &&
  'cat' in (item as Record<string, unknown>);

const categoryKey = (item: Wrapped | TicketCategory): string | number => {
  return isWrapped(item) ? item.cat.id : item.id;
};

const categoryLabel = (item: Wrapped | TicketCategory): string => {
  return isWrapped(item) ? labelFor(item.cat) : labelFor(item);
};

// Unwrap the wrapped item back to TicketCategory for v-model binding
const unwrapValue = (w: unknown): TicketCategory =>
  isWrapped(w) ? w.cat : (w as TicketCategory);
</script>
